import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Form,
    Input,
    Select,
    Button,
    Card,
    Space,
    DatePicker,
    Switch,
    Divider,
    Typography,
    Row,
    Col,
    Table,
    Modal,
    InputNumber,
    Tag,
    Alert,
    Spin,
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    SaveOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    ArrowLeftOutlined,
    GlobalOutlined,
    AppstoreOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'store';
import {
    createGlobalCampaign,
    updateGlobalCampaign,
    fetchGlobalCampaign,
    selectCurrentGlobalCampaign,
    selectGlobalCampaignsLoading,
    selectGlobalCampaignsError,
    setCurrentGlobalCampaign,
} from 'store/slices/globalCampaigns.slice';
import {
    fetchGlobalSegments,
    selectGlobalSegments,
} from 'store/slices/globalSegments.slice';
import {
    fetchApplications,
    selectApplications,
} from 'store/slices/applications.slice';
import { PushType } from 'types/push.types';
import {
    GlobalCampaignCreateRequest,
    GlobalCampaignUpdateRequest,
} from 'types/global_campaigns.types';
import { notify } from 'utils/helpers';
import { ROUTES } from 'config/routes';
import PushTemplateLoader, { LoadedPush } from 'components/push/PushTemplateLoader';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface CampaignPushForm {
    key: string;
    sequence_order: number;
    delay_after_previous?: number;
    title: string;
    body: string;
    image_url?: string;
    click_url?: string;
    custom_data?: Record<string, any>;
    segment_id?: number;
}

const GlobalCampaignForm: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { id } = useParams<{ id: string }>();
    const [form] = Form.useForm();
    const [pushForm] = Form.useForm();

    const currentCampaign = useAppSelector(selectCurrentGlobalCampaign);
    const isLoading = useAppSelector(selectGlobalCampaignsLoading);
    const error = useAppSelector(selectGlobalCampaignsError);
    const applications = useAppSelector(selectApplications) || [];
    const globalSegments = useAppSelector(selectGlobalSegments) || [];

    const [pushes, setPushes] = useState<CampaignPushForm[]>([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingPush, setEditingPush] = useState<CampaignPushForm | null>(null);

    const isEditMode = !!id;
    useEffect(() => {
        dispatch(fetchApplications({ limit: 1000 }));
        dispatch(fetchGlobalSegments());

        if (isEditMode && id) {
            dispatch(fetchGlobalCampaign(Number(id)));
        }

        return () => {
            dispatch(setCurrentGlobalCampaign(null));
        };
    }, [dispatch, id, isEditMode]);
    useEffect(() => {
        if (currentCampaign && isEditMode) {
            form.setFieldsValue({
                name: currentCampaign.name,
                description: currentCampaign.description,
                push_type: currentCampaign.push_type,
                target_app_ids: currentCampaign.target_app_ids || [],
                segment_id: currentCampaign.segment_id,
                is_active: currentCampaign.is_active,
                dateRange: [
                    dayjs(currentCampaign.start_date),
                    currentCampaign.end_date ? dayjs(currentCampaign.end_date) : null,
                ],
            });

            if (currentCampaign.pushes) {
                setPushes(
                    currentCampaign.pushes.map(
                        (push, index): CampaignPushForm => ({
                            key: `push-${index}`,
                            sequence_order: push.sequence_order,
                            delay_after_previous: push.delay_after_previous,
                            title: push.title,
                            body: push.body,
                            image_url: push.image_url || '',
                            click_url: push.click_url || '',
                            custom_data: push.custom_data,
                            segment_id: push.segment_id,
                        }),
                    ),
                );
            }
        }
    }, [currentCampaign, isEditMode, form]);

    const handleAddPush = () => {
        setEditingPush(null);
        pushForm.resetFields();
        setModalVisible(true);
    };

    const handleLoadFromTemplate = (
        loadedPushes: LoadedPush[],
        templateName?: string,
        targetLang?: string,
    ) => {
        if (templateName && !form.getFieldValue('name')) {
            const nameWithLang = targetLang
                ? `${templateName} - ${targetLang.toUpperCase()}`
                : templateName;
            form.setFieldsValue({
                name: nameWithLang,
                description: nameWithLang,
            });
        }

        const newPushes: CampaignPushForm[] = loadedPushes.map((p, i) => ({
            key: `push-template-${Date.now()}-${i}`,
            sequence_order: pushes.length + i + 1,
            title: p.title,
            body: p.body,
            image_url: p.image_url ?? '',
            delay_after_previous: p.delay_after_previous,
        }));
        setPushes(prev => [...prev, ...newPushes]);
    };

    const handleEditPush = (push: CampaignPushForm) => {
        setEditingPush(push);
        pushForm.setFieldsValue({
            ...push,
            custom_data: push.custom_data
                ? JSON.stringify(push.custom_data, null, 2)
                : '',
        });
        setModalVisible(true);
    };

    const handleDeletePush = (key: string) => {
        setPushes(prev => prev.filter(p => p.key !== key));
    };

    const handleMovePush = (index: number, direction: 'up' | 'down') => {
        const newPushes = [...pushes];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= newPushes.length) return;

        [newPushes[index], newPushes[targetIndex]] = [
            newPushes[targetIndex],
            newPushes[index],
        ];
        newPushes.forEach((p, idx) => {
            p.sequence_order = idx + 1;
        });
        setPushes(newPushes);
    };

    const handlePushModalOk = async () => {
        try {
            const values = await pushForm.validateFields();

            let customData = undefined;
            if (values.custom_data) {
                try {
                    customData = JSON.parse(values.custom_data);
                } catch {
                    notify.error('Неверный формат JSON в Custom Data');
                    return;
                }
            }

            const pushData: CampaignPushForm = {
                key: editingPush?.key || `push-${Date.now()}`,
                sequence_order: editingPush?.sequence_order || pushes.length + 1,
                title: values.title,
                body: values.body,
                image_url: values.image_url || undefined,
                click_url: values.click_url || undefined,
                delay_after_previous: values.delay_after_previous,
                segment_id: values.segment_id,
                custom_data: customData,
            };

            if (editingPush) {
                setPushes(prev =>
                    prev.map(p => (p.key === editingPush.key ? pushData : p)),
                );
            } else {
                setPushes(prev => [...prev, pushData]);
            }

            setModalVisible(false);
            pushForm.resetFields();
        } catch (err) {
            console.error('Validation failed:', err);
        }
    };

    const handleSubmit = async (values: any) => {
        if (isEditMode) {
            const updatePayload: GlobalCampaignUpdateRequest = {
                name: values.name,
                description: values.description,
                is_active: values.is_active,
            };
            try {
                await dispatch(
                    updateGlobalCampaign({ id: Number(id), data: updatePayload }),
                ).unwrap();
                notify.success('Глобальная кампания обновлена');
                navigate(ROUTES.GLOBAL_CAMPAIGNS);
            } catch (err: any) {
                notify.error(err.message || err || 'Ошибка при сохранении');
            }
            return;
        }
        if (!values.target_app_ids || values.target_app_ids.length === 0) {
            notify.error('Выберите минимум одно приложение для рассылки');
            return;
        }
        if (pushes.length === 0) {
            notify.error('Добавьте хотя бы одно push-уведомление');
            return;
        }

        const [startDate, endDate] = values.dateRange || [];

        const payload: GlobalCampaignCreateRequest = {
            target_app_ids: values.target_app_ids,
            name: values.name,
            description: values.description,
            push_type: values.push_type,
            segment_id: values.segment_id,
            start_date: startDate.toISOString(),
            end_date: endDate ? endDate.toISOString() : undefined,
            is_active: values.is_active ?? true,
            pushes: pushes.map(push => ({
                sequence_order: push.sequence_order,
                delay_after_previous: push.delay_after_previous,
                title: push.title,
                body: push.body,
                image_url: push.image_url,
                click_url: push.click_url,
                custom_data: push.custom_data,
                segment_id: push.segment_id,
            })),
        };

        try {
            await dispatch(createGlobalCampaign(payload)).unwrap();
            notify.success('Глобальная кампания создана');
            navigate(ROUTES.GLOBAL_CAMPAIGNS);
        } catch (err: any) {
            notify.error(err.message || err || 'Ошибка при создании');
        }
    };

    const pushColumns: ColumnsType<CampaignPushForm> = [
        {
            title: '№',
            dataIndex: 'sequence_order',
            key: 'sequence_order',
            width: 50,
            align: 'center',
        },
        {
            title: 'Заголовок',
            dataIndex: 'title',
            key: 'title',
            render: (title: string, record) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{title}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                        {record.body}
                    </div>
                    {record.click_url && (
                        <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 4 }}>
                            → {record.click_url}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Сегмент',
            dataIndex: 'segment_id',
            key: 'segment_id',
            width: 180,
            render: (segmentId?: number) => {
                if (!segmentId) {
                    return <Tag>Сегмент кампании</Tag>;
                }
                const segment = globalSegments.find(s => s.id === segmentId);
                return (
                    <Tag color="purple">{segment ? segment.name : `ID: ${segmentId}`}</Tag>
                );
            },
        },
        {
            title: 'Задержка',
            dataIndex: 'delay_after_previous',
            key: 'delay',
            width: 100,
            align: 'center',
            render: (delay?: number) => (delay ? `${delay} мин` : 'Сразу'),
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 160,
            render: (_, record, index) => (
                <Space>
                    <Button
                        type="text"
                        size="small"
                        icon={<ArrowUpOutlined />}
                        disabled={index === 0}
                        onClick={() => handleMovePush(index, 'up')}
                    />
                    <Button
                        type="text"
                        size="small"
                        icon={<ArrowDownOutlined />}
                        disabled={index === pushes.length - 1}
                        onClick={() => handleMovePush(index, 'down')}
                    />
                    <Button type="link" size="small" onClick={() => handleEditPush(record)}>
                        Изменить
                    </Button>
                    <Button
                        type="link"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeletePush(record.key)}
                    />
                </Space>
            ),
        },
    ];

    if (isLoading && isEditMode && !currentCampaign) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" tip="Загрузка кампании..." />
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <Row align="middle" style={{ marginBottom: 24 }}>
                <Col>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate(ROUTES.GLOBAL_CAMPAIGNS)}
                        style={{ marginRight: 16 }}
                    >
                        Назад
                    </Button>
                </Col>
                <Col>
                    <Space>
                        <GlobalOutlined style={{ color: 'var(--accent-purple)', fontSize: 24 }} />
                        <Title level={2} style={{ margin: 0 }}>
                            {isEditMode
                                ? 'Редактирование глобальной кампании'
                                : 'Создание глобальной кампании'}
                        </Title>
                    </Space>
                </Col>
            </Row>

            {error && (
                <Alert
                    message="Ошибка"
                    description={error}
                    type="error"
                    closable
                    style={{ marginBottom: 24 }}
                />
            )}

            {isEditMode && (
                <Alert
                    message="Редактирование доступно только для метаданных"
                    description="В режиме редактирования можно изменить только название, описание и статус активности. Состав push-уведомлений и список target apps зафиксированы при создании."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    push_type: PushType.REGULAR,
                    is_active: true,
                    target_app_ids: [],
                }}
            >
                <Row gutter={24}>
                    <Col xs={24} lg={16}>
                        <Card title="Основная информация" style={{ marginBottom: 16 }}>
                            <Form.Item
                                name="name"
                                label="Название кампании"
                                rules={[
                                    { required: true, message: 'Введите название' },
                                    { min: 3, message: 'Минимум 3 символа' },
                                ]}
                            >
                                <Input placeholder="Например: Welcome серия — все приложения" />
                            </Form.Item>

                            <Form.Item
                                name="description"
                                label="Описание"
                                rules={[{ required: true, message: 'Введите описание' }]}
                            >
                                <TextArea rows={3} placeholder="Краткое описание кампании" />
                            </Form.Item>

                            <Form.Item
                                name="target_app_ids"
                                label="Приложения для рассылки"
                                rules={[
                                    {
                                        required: !isEditMode,
                                        message: 'Выберите минимум одно приложение',
                                    },
                                ]}
                                tooltip={
                                    isEditMode
                                        ? 'Список приложений зафиксирован при создании, изменить нельзя'
                                        : 'Каждый push будет отправлен в каждое выбранное приложение'
                                }
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Выберите приложения"
                                    disabled={isEditMode}
                                    maxTagCount="responsive"
                                    showSearch
                                    optionFilterProp="label"
                                    options={applications.map(app => ({
                                        value: app.id,
                                        label: app.name,
                                    }))}
                                    dropdownRender={menu => (
                                        <>
                                            <Space style={{ padding: '4px 8px' }}>
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    onClick={() =>
                                                        form.setFieldsValue({
                                                            target_app_ids: applications.map(a => a.id),
                                                        })
                                                    }
                                                >
                                                    Выбрать все ({applications.length})
                                                </Button>
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    onClick={() => form.setFieldsValue({ target_app_ids: [] })}
                                                >
                                                    Очистить
                                                </Button>
                                            </Space>
                                            <Divider style={{ margin: '4px 0' }} />
                                            {menu}
                                        </>
                                    )}
                                />
                            </Form.Item>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="push_type"
                                        label="Тип уведомлений"
                                        rules={[{ required: true }]}
                                    >
                                        <Select disabled={isEditMode}>
                                            <Select.Option value={PushType.REGULAR}>
                                                Обычные
                                            </Select.Option>
                                            <Select.Option value={PushType.EVENT}>
                                                Событийные
                                            </Select.Option>
                                            <Select.Option value={PushType.SPECIAL}>
                                                Специальные
                                            </Select.Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="segment_id"
                                        label="Глобальный сегмент (по умолчанию)"
                                        tooltip="Сегмент применится ко всем push'ам, у которых не указан свой. Только глобальные сегменты."
                                    >
                                        <Select
                                            placeholder="Все пользователи"
                                            allowClear
                                            disabled={isEditMode}
                                            options={globalSegments.map(seg => ({
                                                value: seg.id,
                                                label: seg.name,
                                            }))}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                name="dateRange"
                                label="Период действия"
                                rules={[
                                    { required: !isEditMode, message: 'Выберите период' },
                                ]}
                            >
                                <RangePicker
                                    showTime
                                    format="YYYY-MM-DD HH:mm"
                                    style={{ width: '100%' }}
                                    placeholder={['Дата начала', 'Дата окончания']}
                                    disabled={isEditMode}
                                />
                            </Form.Item>
                        </Card>

                        <Card
                            title="Push-уведомления в кампании"
                            extra={
                                !isEditMode && (
                                    <Space>
                                        <PushTemplateLoader onLoad={handleLoadFromTemplate} />
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={handleAddPush}
                                        >
                                            Добавить Push
                                        </Button>
                                    </Space>
                                )
                            }
                        >
                            {pushes.length === 0 ? (
                                <div
                                    style={{
                                        textAlign: 'center',
                                        padding: '40px 0',
                                        color: 'var(--text-muted)',
                                    }}
                                >
                                    <Text>Кампания пока не содержит push-уведомлений</Text>
                                    {!isEditMode && (
                                        <div style={{ marginTop: 16 }}>
                                            <Button
                                                type="dashed"
                                                icon={<PlusOutlined />}
                                                onClick={handleAddPush}
                                            >
                                                Добавить первое уведомление
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Table
                                    columns={isEditMode ? pushColumns.slice(0, -1) : pushColumns}
                                    dataSource={pushes}
                                    pagination={false}
                                    size="small"
                                    rowKey="key"
                                />
                            )}
                        </Card>
                    </Col>

                    <Col xs={24} lg={8}>
                        <Card title="Параметры" style={{ marginBottom: 16 }}>
                            <Form.Item
                                name="is_active"
                                label="Статус"
                                valuePropName="checked"
                            >
                                <Switch
                                    checkedChildren="Активна"
                                    unCheckedChildren="Неактивна"
                                />
                            </Form.Item>

                            <Divider />

                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Button
                                    type="primary"
                                    icon={<SaveOutlined />}
                                    htmlType="submit"
                                    loading={isLoading}
                                    size="large"
                                    block
                                >
                                    {isEditMode
                                        ? 'Сохранить изменения'
                                        : 'Создать кампанию'}
                                </Button>
                                <Button
                                    onClick={() => navigate(ROUTES.GLOBAL_CAMPAIGNS)}
                                    block
                                >
                                    Отмена
                                </Button>
                            </Space>
                        </Card>

                        {!isEditMode && (
                            <Card title="Что произойдёт при создании" size="small">
                                <Space direction="vertical" size="small">
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        <AppstoreOutlined /> N приложений
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        × M push'ей
                                    </Text>
                                    <Divider style={{ margin: '8px 0' }} />
                                    <Text strong style={{ fontSize: 13 }}>
                                        = N×M push-сообщений в очереди
                                    </Text>
                                </Space>
                            </Card>
                        )}
                    </Col>
                </Row>
            </Form>
            <Modal
                title={editingPush ? 'Редактирование Push' : 'Добавление Push'}
                open={modalVisible}
                onOk={handlePushModalOk}
                onCancel={() => setModalVisible(false)}
                width={700}
                okText="Сохранить"
                cancelText="Отмена"
            >
                <Form form={pushForm} layout="vertical">
                    <Form.Item
                        name="title"
                        label="Заголовок"
                        rules={[
                            { required: true, message: 'Введите заголовок' },
                            { max: 100, message: 'Максимум 100 символов' },
                        ]}
                    >
                        <Input placeholder="Заголовок уведомления" maxLength={100} />
                    </Form.Item>

                    <Form.Item
                        name="body"
                        label="Текст сообщения"
                        rules={[
                            { required: true, message: 'Введите текст' },
                            { max: 500, message: 'Максимум 500 символов' },
                        ]}
                    >
                        <TextArea
                            placeholder="Текст уведомления"
                            rows={4}
                            maxLength={500}
                            showCount
                        />
                    </Form.Item>

                    <Form.Item name="image_url" label="URL изображения">
                        <Input placeholder="https://example.com/image.jpg" />
                    </Form.Item>

                    <Form.Item
                        name="click_url"
                        label="URL при клике"
                        tooltip="Куда перейдёт пользователь при клике на push"
                    >
                        <Input placeholder="https://example.com/promo" />
                    </Form.Item>

                    <Form.Item
                        name="delay_after_previous"
                        label="Задержка после предыдущего (минуты)"
                        tooltip="Через сколько минут отправить это уведомление после предыдущего. 0 = сразу."
                    >
                        <InputNumber
                            min={0}
                            max={10080}
                            placeholder="0"
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="segment_id"
                        label="Глобальный сегмент (необязательно)"
                        tooltip="Если не указан — используется сегмент уровня кампании. Если и там нет — все пользователи."
                    >
                        <Select
                            placeholder="Использовать сегмент кампании"
                            allowClear
                            options={globalSegments.map(seg => ({
                                value: seg.id,
                                label: seg.name,
                            }))}
                        />
                    </Form.Item>

                    <Form.Item name="custom_data" label="Дополнительные данные (JSON)">
                        <TextArea
                            placeholder='{"key": "value"}'
                            rows={3}
                            style={{ fontFamily: 'monospace' }}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default GlobalCampaignForm;